/**
 * ChatCoreMixin - AI 聊天核心逻辑
 * 包含：消息列表管理、滚动监听、输入监听、发送消息等
 */
import { marked } from "marked";
import hljs from "highlight.js";
import {
  getRobotIcon1Base64,
  getRobotIcon2Base64,
  getUserIconBase64,
} from "@/utils/svgToBase64.js";
import { conversationLogDetail } from "@/aips/canvas/aiCanvasApi.js";

export default {
  data() {
    // 获取SVG Base64 URL
    const robotIcon1 = getRobotIcon1Base64();
    const robotIcon = getRobotIcon2Base64();
    const userIcon = getUserIconBase64();

    // 这里做默认roomName的判断
    const surveySuggest = sessionStorage.surveySuggest
      ? JSON.parse(sessionStorage.surveySuggest)
      : null;
    const roomName =
      surveySuggest && surveySuggest.title
        ? surveySuggest.title > 10
          ? surveySuggest.title.slice(0, 10) + "..."
          : surveySuggest.title
        : this.$t("aiChat.aiAssistantXiaoTu");

    // 保存原始完整的房间名称
    const originalRoomName =
      surveySuggest && surveySuggest.title
        ? surveySuggest.title
        : this.$t("aiChat.aiAssistantXiaoTu");

    return {
      isloading: false,
      isLoadingHistory: false,
      graph: {},
      aliyunOssToken: {},
      currentUserId: "user",
      userScrolling: false,
      lastScrollTop: 0,
      isComposing: false,
      rooms: [
        {
          roomId: "ai-chat",
          roomName: roomName,
          originalRoomName: originalRoomName,
          users: [
            {
              _id: "ai",
              username: this.$t("aiChat.aiAssistant"),
              avatar: robotIcon,
              status: {
                state: this.$t("aiChat.online"),
                lastChanged: this.$t("aiChat.today"),
              },
            },
            {
              _id: "user",
              username: this.$t("aiChat.you"),
              avatar: "",
              status: {
                state: this.$t("aiChat.online"),
                lastChanged: this.$t("aiChat.today"),
              },
            },
          ],
          unreadCount: 0,
          index: 0,
          lastMessage: {
            content: this.$t("aiChat.initialGreeting"),
            senderId: "ai",
            saved: true,
            distributed: true,
            seen: true,
          },
        },
      ],
      messages: [],
      isStreaming: false,
      eventSource: null,
      textMessages: {
        ROOMS_EMPTY: this.$t("aiChat.textMessages.roomsEmpty"),
        ROOM_EMPTY: this.$t("aiChat.textMessages.roomEmpty"),
        NEW_MESSAGES: this.$t("aiChat.textMessages.newMessages"),
        MESSAGE_DELETED: this.$t("aiChat.textMessages.messageDeleted"),
        MESSAGES_EMPTY: this.$t("aiChat.textMessages.messagesEmpty"),
        CONVERSATION_STARTED: this.$t("aiChat.textMessages.conversationStarted"),
        TYPE_MESSAGE: this.$t("aiChat.textMessages.typeMessage"),
        SEARCH: this.$t("aiChat.textMessages.search"),
        IS_ONLINE: this.$t("aiChat.textMessages.isOnline"),
        LAST_SEEN: this.$t("aiChat.textMessages.lastSeen"),
        IS_TYPING: this.$t("aiChat.textMessages.isTyping"),
      },
      chatStyles: {
        general: {
          color: "#0D0E0F",
          colorSpinner: "#649ff6",
        },
        header: {
          background:
            "linear-gradient(270deg, #84e2f4 0.48%, #649ff6 56.73%, #8c82f9 100%)",
          colorRoomName: "#ffffff",
          colorRoomInfo: "#ffffff",
        },
        footer: {
          background: "#fff",
        },
        content: {
          background:
            "linear-gradient(180deg, #f2f7ff 0%, #f2fdff 49.52%, #fcfaff 100%)",
        },
        messagesList: {
          background: "#f7f9fb",
        },
        message: {
          background: "#ffffff",
          backgroundMe: "#c4edff",
          color: "#0D0E0F",
          colorMe: "#ffffff",
          maxWidth: "95%",
          containerMaxWidth: "95%",
        },
      },
      currentAiMessage: null,
      markdownContent: "",
      isResizing: false,
      hasInputContent: false,
      taskId: null,
      knowledgeEnabled: false,
      conversationIdNow: null,
    };
  },

  mounted() {
    // 初始化 sessionStorage 中的房间名称
    if (sessionStorage.surveySuggest) {
      let surveySuggest = JSON.parse(sessionStorage.surveySuggest);
      console.log(surveySuggest.title);
      this.changeRoomName(surveySuggest.title);
    }

    // 配置 marked 选项，添加代码高亮功能
    marked.setOptions({
      breaks: true,
      gfm: true,
      sanitize: false,
      highlight: function (code, lang) {
        const language = hljs.getLanguage(lang) ? lang : "plaintext";
        return hljs.highlight(code, { language }).value;
      },
    });

    console.log("我是输入框消息");
    console.log(this.$route.query.inputContent);

    // 如果没有从父组件接收到消息列表，则显示默认欢迎消息
    if (this.messageList.length === 0) {
      if (this.$route.query.conversationId && this.aiCanvasRobot) {
        let id = this.$route.query.conversationId;
        this.conversationIdNow = this.$route.query.conversationId;
        this.getMessage(id);
      } else if (this.$route.query.inputContent && this.aiCanvasRobot) {
        let inputContent = "";
        try {
          inputContent = JSON.parse(this.$route.query.inputContent);
        } catch (e) {
          inputContent = this.$route.query.inputContent;
        }
        this.changeRoomName(inputContent, "start");
        this.$nextTick(() => {
          setTimeout(() => {
            const container = this.$el || document;
            const textarea = container.querySelector(".vac-textarea");
            if (textarea) {
              textarea.value = JSON.parse(this.$route.query.inputContent);
              this.checkInputContent();
              this.triggerSendMessage();
              setTimeout(() => {
                this.$router.replace({
                  path: this.$route.path,
                  query: { inputContent: null },
                });
              }, 1000);
            }
          }, 600);
        });
      } else {
        this.createNewAiChat();
      }
    }

    // 添加消息操作图标的事件监听
    this.$nextTick(() => {
      this.addMessageActionIcons();

      // 监听新消息添加
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "childList" && mutation.addedNodes.length) {
            this.addMessageActionIcons();
          }
        });
      });

      const config = { childList: true, subtree: true };
      const observerContainer = this.$el || document;
      const messagesContainer = observerContainer.querySelector(".vac-message-container");
      if (messagesContainer) {
        observer.observe(messagesContainer, config);
      }

      // 添加输入框内容变化监听
      this.setupInputListener();

      // 添加滚动事件监听
      this.setupScrollListener();

      // 初始滚动到底部
      this.scrollToBottom();
    });
  },

  methods: {
    // ========== 聊天创建与房间管理 ==========
    createNewAiChat() {
      let data = [
        {
          content: this.aiCanvasRobot
            ? this.$t("aiChat.welcomeMessageRobot")
            : this.$t("aiChat.welcomeMessageFull"),
          id: "ai-1",
          senderId: "ai",
        },
      ];
      this.conversationIdNow = null;
      let name = this.aiCanvasRobot
        ? this.$t("aiChat.aiAssistantXiaoTu")
        : this.$t("aiChat.aiAssistant");
      this.changeRoomName(name);
      this.updataMessageList(data);
    },

    changeRoomName(inputContent, start) {
      if (start) {
        if (sessionStorage.surveySuggest === undefined) {
          let surveySuggest = { title: inputContent };
          sessionStorage.surveySuggest = JSON.stringify(surveySuggest);
        } else {
          let surveySuggest = JSON.parse(sessionStorage.surveySuggest);
          surveySuggest.title = inputContent;
          sessionStorage.surveySuggest = JSON.stringify(surveySuggest);
        }
      }
      if (typeof inputContent === "string") {
        let displayName =
          inputContent.length > 10
            ? inputContent.slice(0, 10) + "..."
            : inputContent;
        this.rooms[0].roomName = displayName;
        this.rooms[0].originalRoomName = inputContent;
      }
    },

    // ========== 会话记录管理 ==========
    getMessage(id) {
      this.isLoadingHistory = true;
      conversationLogDetail(id)
        .then((res) => {
          console.log(res);
          if (res.code == 200) {
            console.log("我是详细的会话内容");
            console.log(res.data);
            let list = [...res.data];
            this.updataMessageList(list);
          }
          this.isLoadingHistory = false;
        })
        .catch(() => {
          this.isLoadingHistory = false;
        });
    },

    handleGetMessageList(item) {
      console.log("执行-------------------------111111");
      this.getMessage(item.conversationId);
      this.changeRoomName(item.title);
    },

    // ========== 消息列表更新 ==========
    updataMessageList(newVal) {
      console.log("我是更新的消息列表");
      console.log(newVal);
      this.isLoadingHistory = this.aiCanvasRobot;

      // 清空现有消息
      this.messages = [];
      // 清空旧的点赞/点踩状态
      this.messageFeedbackStates = {};

      // 如果有消息且第一条消息有标题，更新房间名称
      if (newVal && newVal.length > 0 && newVal[0].title) {
        this.changeRoomName(newVal[0].title);
      }

      // 处理从父组件接收的消息列表
      newVal.forEach((msg) => {
        const msgId =
          msg.id ||
          msg._id ||
          `msg-${Math.random().toString(36).substring(2, 15)}`;

        const avatar =
          msg.senderId === "ai"
            ? this.aiCanvasRobot
              ? getRobotIcon2Base64()
              : getRobotIcon1Base64()
            : "";

        const rawContent = String(msg.content == null ? "" : msg.content);
        const isAiMessage = msg.senderId === "ai";
        const content = isAiMessage
          ? `<div class="markdown-body custom-markdown">${this.convertMarkdownToHtml(
              rawContent
            )}</div>`
          : this.formatUserMessageContent(rawContent);

        const messageObj = {
          _id: msgId,
          content: content,
          rawContent: isAiMessage ? undefined : rawContent,
          senderId: isAiMessage ? "ai" : "user",
          username:
            isAiMessage
              ? this.$t("aiChat.aiAssistant")
              : this.$t("aiChat.you"),
          avatar: avatar,
          date: new Date().toLocaleDateString(),
          saved: true,
          distributed: true,
          seen: true,
          html: true,
          taskId: isAiMessage ? msg.taskId : null,
        };

        // 将后端返回的评价结果写入本地 state
        if (msg.senderId === "ai" && msg.evaluateStatus === "EVALUATED") {
          let mappedStatus = null;
          if (msg.evaluateResult === "GOOD") mappedStatus = "like";
          else if (msg.evaluateResult === "BAD") mappedStatus = "dislike";

          if (mappedStatus) {
            this.messageFeedbackStates = {
              ...this.messageFeedbackStates,
              [msgId]: { status: mappedStatus },
            };
          }
        }

        this.messages.push(messageObj);
      });

      this.$nextTick(() => {
        this.scrollToBottom();
        this.addMessageActionIcons();
        setTimeout(() => {
          this.isLoadingHistory = false;
        }, 500);
      });
    },

    updateLastAiMessage() {
      this.messages = [...this.messages];
      this.$nextTick(() => {
        this.addMessageActionIcons();
        this.scrollToBottom();
      });
    },

    // ========== 滚动监听 ==========
    setupScrollListener() {
      setTimeout(() => {
        const container = this.$el || document;
        const messageContainer = container.querySelector(".vac-container-scroll");
        if (messageContainer) {
          messageContainer.addEventListener("scroll", this.handleScroll);
          messageContainer.addEventListener("wheel", this.handleWheel);
          messageContainer.addEventListener("touchstart", this.handleTouch);
        }
      }, 500);
    },

    handleScroll(event) {
      const container = event.target;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      if (scrollHeight - scrollTop - clientHeight < 10) {
        this.userScrolling = false;
      } else if (scrollTop < this.lastScrollTop) {
        this.userScrolling = true;
        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
          this.userScrolling = false;
        }, 3000);
      }
      this.lastScrollTop = scrollTop;
    },

    handleWheel() {
      this.userScrolling = true;
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        this.userScrolling = false;
      }, 3000);
    },

    handleTouch() {
      this.userScrolling = true;
    },

    scrollToBottom() {
      setTimeout(() => {
        const container = this.$el || document;
        const messageContainer = container.querySelector(".vac-container-scroll");
        if (messageContainer && !this.userScrolling) {
          messageContainer.scrollTop = messageContainer.scrollHeight;
        }
      }, 50);
    },

    // ========== 输入框监听 ==========
    setupInputListener() {
      setTimeout(() => {
        const container = this.$el || document;
        const textarea = container.querySelector(".vac-textarea");
        if (textarea) {
          this.checkInputContent();
          textarea.addEventListener("input", this.checkInputContent);
          textarea.addEventListener("compositionstart", () => {
            this.isComposing = true;
          });
          textarea.addEventListener("compositionend", () => {
            this.isComposing = false;
            this.checkInputContent();
          });
          textarea.addEventListener("keydown", this.handleKeydown);
        }
      }, 500);
    },

    handleKeydown(event) {
      if (event.isComposing || this.isComposing) {
        return;
      }
      if (event.key === "Enter" && !this.isStreaming) {
        event.preventDefault();
        this.checkInputContent();
        this.triggerSendMessage();
      }
    },

    checkInputContent() {
      const container = this.$el || document;
      const textarea = container.querySelector(".vac-textarea");
      if (textarea) {
        this.hasInputContent = textarea.value.trim().length > 0;
      }
    },

    // ========== 发送消息 ==========
    triggerSendMessage() {
      console.log("消息列表");
      console.log(this.messages);
      if (this.isStreaming || this.isloading) {
        return;
      }
      const container = this.$el || document;
      const textarea = container.querySelector(".vac-textarea");
      if (textarea && textarea.value.trim()) {
        const content = textarea.value;
        this.onMessageWasSent({ content });
        textarea.value = "";
        textarea.style.height = "40px";
        this.checkInputContent();
      }
    },

    onMessageWasSent({ content }) {
      console.log("我是内容");
      console.log(content);

      if (this.isStreaming || this.isloading) {
        return;
      }

      if (!content.trim()) return;

      const container = this.$el || document;
      const textarea = container.querySelector(".vac-textarea");
      if (textarea) {
        textarea.style.height = "40px";
      }

      const userMessage = {
        _id:
          "user-msg-" +
          Math.random().toString(36).substring(2, 15) +
          Math.random().toString(36).substring(2, 15),
        content: this.formatUserMessageContent(content),
        rawContent: content,
        senderId: "user",
        username: this.$t("aiChat.you"),
        avatar: "",
        date: new Date().toLocaleDateString(),
        saved: true,
        distributed: true,
        seen: true,
        html: true,
      };

      this.messages.push(userMessage);
      this.sendToAI(content);

      this.$nextTick(() => {
        this.addMessageActionIcons();
        this.scrollToBottom();
      });
    },

    // ========== 工具栏操作 ==========
    handleKnowledgeBase() {
      console.log("图链知识库按钮点击");
      this.knowledgeEnabled = true;
    },

    handleAddressAnalysis() {
      console.log("地址研判分析按钮点击");
      this.knowledgeEnabled = false;
    },

    historicalRecords() {
      console.log("历史会话");
    },
  },

  beforeDestroy() {
    if (this.eventSource) this.eventSource.close();

    const container = this.$el || document;
    const messageContainer = container.querySelector(".vac-container-scroll");
    if (messageContainer) {
      messageContainer.removeEventListener("scroll", this.handleScroll);
      messageContainer.removeEventListener("wheel", this.handleWheel);
      messageContainer.removeEventListener("touchstart", this.handleTouch);
    }

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  },
};
